import { useState, memo, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { NAV_ITEMS, CALENDLY_URL, GUESTLY_URL } from '@/src/constants';
import { useScrollPosition } from '@/src/hooks';
import { cn } from '@/src/utils';
import { Logo, Button } from '@/src/components/ui';

function Navbar() {
  const { isScrolled } = useScrollPosition(20);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mobileExpandedIndex, setMobileExpandedIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Handle navigation to hash links (like /#precios)
  const handleHashLink = (href: string) => {
    if (href.startsWith('/#')) {
      const hash = href.substring(1); // Remove leading /
      const isOnHome = location.pathname === '/';

      if (isOnHome) {
        // Already on home, just scroll to element
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(hash.replace('#', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
      return true;
    }
    return false;
  };

  const handleNavClick = (href: string, e?: React.MouseEvent) => {
    if (handleHashLink(href)) {
      e?.preventDefault();
    }
  };

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        isScrolled
          ? 'bg-[#050505]/80 backdrop-blur-md border-white/10 py-4'
          : 'bg-transparent border-transparent py-6'
      )}
    >
      <div className="container mx-auto px-6 flex justify-between items-center relative">
        <Logo />

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item, index) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {item.href.startsWith('/#') ? (
                <button
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-all duration-300 py-2 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-transform duration-300',
                        hoveredIndex === index && 'rotate-180 text-white'
                      )}
                    />
                  )}
                </button>
              ) : item.href.startsWith('/') ? (
                <Link
                  to={item.href}
                  className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-all duration-300 py-2 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-transform duration-300',
                        hoveredIndex === index && 'rotate-180 text-white'
                      )}
                    />
                  )}
                </Link>
              ) : (
                <a
                  href={item.href}
                  className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-all duration-300 py-2 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown
                      size={14}
                      className={cn(
                        'transition-transform duration-300',
                        hoveredIndex === index && 'rotate-180 text-white'
                      )}
                    />
                  )}
                </a>
              )}

              {/* Desktop Dropdown */}
              <AnimatePresence>
                {item.children && hoveredIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden p-2 z-50"
                  >
                    <div className="flex flex-col gap-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-4 py-3 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200 hover:pl-6"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href={GUESTLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-2"
          >
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            Guestly
          </a>
          <Button size="sm" onClick={() => window.open(CALENDLY_URL, '_blank')}>
            Demo Gratuita
          </Button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden text-white z-[110] relative hover:text-gray-300 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-[#050505] z-[100] flex flex-col pt-24 px-6 md:hidden overflow-y-auto no-scrollbar"
          >
            <div className="flex flex-col gap-2">
              {NAV_ITEMS.map((item, index) => (
                <div key={item.label} className="border-b border-white/5 last:border-0 pb-2">
                  <div
                    className="flex justify-between items-center py-4 text-lg font-medium text-gray-200 cursor-pointer active:text-white transition-colors"
                    onClick={() => {
                      if (item.children) {
                        setMobileExpandedIndex(mobileExpandedIndex === index ? null : index);
                      } else {
                        handleNavClick(item.href);
                        closeMobileMenu();
                      }
                    }}
                  >
                    {item.children ? (
                      <span className={mobileExpandedIndex === index ? 'text-white' : ''}>
                        {item.label}
                      </span>
                    ) : item.href.startsWith('/#') ? (
                      <span>{item.label}</span>
                    ) : (
                      <Link to={item.href} onClick={closeMobileMenu}>
                        {item.label}
                      </Link>
                    )}

                    {item.children && (
                      <ChevronDown
                        size={20}
                        className={cn(
                          'transition-transform duration-300 text-gray-500',
                          mobileExpandedIndex === index && 'rotate-180 text-white'
                        )}
                      />
                    )}
                  </div>

                  {/* Mobile Collapsible Submenu */}
                  <AnimatePresence>
                    {item.children && mobileExpandedIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-4 pl-4 pb-4 border-l border-white/10 ml-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.label}
                              to={child.href}
                              className="text-gray-500 hover:text-white text-sm flex items-center gap-3 py-1 transition-colors"
                              onClick={closeMobileMenu}
                            >
                              <div className="w-1 h-1 bg-gray-600 rounded-full" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-4 pb-10">
              <Button
                size="lg"
                className="w-full"
                onClick={() => window.open(CALENDLY_URL, '_blank')}
              >
                Probar Demo
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default memo(Navbar);
